import { useEffect, useState } from "react";
import type { FavouritesMenuGroup } from "@/types/domain";
import { popupCenter } from "@/utils/popup";
import { fetchFavouritesMenu } from "@/api/menu/api";
import { flyoutRowClass } from "./flyoutStyles";
import { HeaderTip, NAV_TIP_DELAY } from "../HeaderTip";

const OptionalTip = ({ label, children }: { label?: string | null; children: React.ReactElement }) =>
  label ? <HeaderTip label={label} side="right" delayDuration={NAV_TIP_DELAY}>{children}</HeaderTip> : children;

export const FavouritesSubMenu = ({ onActivated }: { onActivated?: () => void }) => {
  const [groups, setGroups] = useState<FavouritesMenuGroup[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openIds, setOpenIds] = useState<string[]>([]);

  const load = () => {
    fetchFavouritesMenu(window.location.href)
      .then((groups) => {
        setGroups(groups);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  };

  useEffect(() => {
    load();
    const w = window as any;
    const previous = w.reloadFavouritePanel;
    w.reloadFavouritePanel = load;
    return () => {
      if (w.reloadFavouritePanel !== load) return;
      if (previous) w.reloadFavouritePanel = previous;
      else delete w.reloadFavouritePanel;
    };
  }, []);

  if (!loaded) {
    return <li className="px-2 py-2 text-sm text-slate-500">Loading…</li>;
  }

  const toggle = (id: string) =>
    setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <>
      {groups.map((group) => (
        <li key={group.groupLabel} role="presentation">
          <div className="px-2 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group.groupLabel}</div>
          <ul>
            {group.categories.map((cat, index) => {
              const catKey = `${group.groupLabel}:${index}`;
              const isOpen = openIds.includes(catKey);
              return (
                <li key={catKey} className="mb-1">
                  <button
                    type="button"
                    data-flyout-item
                    aria-expanded={isOpen}
                    onClick={() => toggle(catKey)}
                    className="flex w-full items-center rounded px-3 py-[7px] text-left text-[13px] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-[var(--layout-side-nav-active-bg)] focus-visible:ring-offset-1"
                    style={{ backgroundColor: cat.colour, color: cat.foreColour }}
                  >
                    <span className="truncate">{cat.name}</span>
                  </button>
                  {isOpen && (
                    <ul className="pt-0.5 pb-1.5">
                      {cat.favourites.map((fav) => (
                        <li key={fav.id}>
                          <OptionalTip label={fav.description}>
                          <button
                            type="button"
                            data-flyout-item
                            onClick={() => {
                              popupCenter(`/handlers/FavouriteEnquiry.aspx?favouriteId=${fav.id}`, "vectaEnquiry", 1024, 768);
                              onActivated?.();
                            }}
                            className={flyoutRowClass(false)}
                          >
                            <span className="truncate">{fav.name}</span>
                          </button>
                          </OptionalTip>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
      {groups.length === 0 && (
        <li className="px-2 py-2 text-sm text-slate-500">No favourites</li>
      )}
    </>
  );
}
